import useConnection from '@shared/hooks/useConnetions/useConnection';
import useToast from '@shared/hooks/useToast';
import clearDataStorage from '@shared/utils/auth/clearDataStorage';
import getDataStorage from '@shared/utils/auth/getDataStorage';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import perfiles from './perfiles';

const PerfilesContext = createContext();

const PERFIL_KEY = 'perfil_conductor';

export const PerfilesProvider = ({ children }) => {
    const connection = useConnection();
    const navigate = useNavigate();
    // El perfil se PERSISTE (como userData): así un arranque en frío SIN señal no obliga a
    // pedirlo al server (que fallaría) ni desloguea al chofer. Crítico para uso en el campo.
    const [perfil, setPerfilState] = useState(() => {
        try { return localStorage.getItem(PERFIL_KEY) || null; } catch { return null; }
    });
    const setPerfil = (p) => {
        setPerfilState(p);
        try {
            if (p) localStorage.setItem(PERFIL_KEY, p);
            else localStorage.removeItem(PERFIL_KEY);
        } catch { /* storage no disponible */ }
    };
    const [loading, setLoading] = useState(true);
    const { newToast } = useToast();
    const previousPerfil = useRef(null);

    const forceLogout = async (errorMessage = 'Sesión inválida') => {
        newToast(errorMessage, 'error');

        try {
            await connection({
                url: `/auth/api/v1/auth/local/logout`,
                api: 'NITRO4',
                method: 'GET'
            });
        } catch (e) {
            console.error('Error al desloguear forzadamente:', e);
        };

        clearDataStorage();
        setPerfil('');
        navigate('/login');
    };

    const fetchPerfil = async () => {
        try {
            const dataUsuario = await connection({
                url: `/perfiles/obtenerPerfil`,
                label: 'solicitar perfiles'
            });
            return dataUsuario;
        } catch (error) {
            console.error(error);
        };
    };

    const validatePerfil = async () => {
        try {
            const serverUser = await fetchPerfil();
            const storage = getDataStorage();

            const samePerfil = serverUser.perfil === perfil;
            const sameId = serverUser.id === storage?.id;

            if (!samePerfil || !sameId) {
                throw new Error('Se detectaron credenciales inválidas');
            };
            return serverUser;
        } catch (e) {
            console.error('Validación de perfil fallida:', e);
            await forceLogout(e.message);
        };
    };

    const getAndSetPerfil = async () => {
        try {
            setLoading(true);
            const serverUser = await fetchPerfil();

            if (!serverUser) {
                // Sin respuesta del server: si estamos OFFLINE y hay perfil cacheado, seguimos
                // con ese (no deslogueamos al chofer en el campo sin señal).
                if (!navigator.onLine && perfil) {
                    return { estado: 1, response: { perfil } };
                }
                return { perfil: null, estado: 0 };
            }

            if (!serverUser.estado) {
                return serverUser;
            };
            setPerfil(serverUser.response.perfil);
            previousPerfil.current = serverUser.response.perfil;
            return serverUser;
        } catch (e) {
            // Falla de red estando offline: usar el perfil cacheado en vez de desloguear.
            if (!navigator.onLine && perfil) {
                return { estado: 1, response: { perfil } };
            }
            console.error('Error al obtener perfil:', e);
            await forceLogout(e.message);
        } finally {
            setLoading(false);
        };
    };

    // useEffect(() => {
    //     if (perfil && perfil !== previousPerfil.current) {
    //         previousPerfil.current = perfil;
    //         validatePerfil();
    //     };
    // }, [perfil, previousPerfil.current]);

    return (
        <PerfilesContext.Provider
            value={{
                loading,
                perfil,
                perfiles,
                getAndSetPerfil,
                validatePerfil
            }}
        >
            {children}
        </PerfilesContext.Provider>
    );
};

export const usePerfilesContext = () => useContext(PerfilesContext);
